import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGeoInterestedUiPreferences } from '@hcs/huell';
import {
  CERBERUS_STATS_FIELDS,
  CERBERUS_STATS_FILTER_TYPE,
  CerberusStatsFields,
  SORT_ORDER,
} from '@hcs/types';

import { useCerberusStatsAllMsasWithHpi } from '../hooks/useCerberusStatsAllMsasWithHpi';
import { filterList, sortList } from '../utils/cerberusStatsTable.utils';

import { useCerberusStatsFilters } from './';

const LEGACY_QUERY_PARAM_NAMES = {
  Search: 'msa-search',
  Sort: 'msa-sort',
  Order: 'msa-order',
} as const;

type Options = {
  sort: {
    field: CerberusStatsFields;
    order: SORT_ORDER;
  };
};

const sortParamToCerberusStatsSortField = (
  msaSortableField: string | null,
): CerberusStatsFields | null => {
  if (msaSortableField === null) {
    return null;
  } else if (msaSortableField === 'gross_yield') {
    return CERBERUS_STATS_FIELDS.grossYieldMedian;
  } else if (msaSortableField === 'hpi_forecast') {
    return CERBERUS_STATS_FIELDS.return1YearForecast;
  } else if (msaSortableField === 'months_of_supply') {
    return CERBERUS_STATS_FIELDS.monthsOfSupplyMedian;
  } else if (msaSortableField === 'msa_name') {
    return CERBERUS_STATS_FIELDS.displayName;
  } else if (msaSortableField === 'number_of_listings') {
    return CERBERUS_STATS_FIELDS.listingsOnMarketCount;
  } else if (msaSortableField === 'sale_to_list_price') {
    return CERBERUS_STATS_FIELDS.saleToListPriceOriginalMedian;
  }
  return null;
};

const sortOrderParamToSortOrderEnum = (
  msaSortOrder: string | null,
): SORT_ORDER | null => {
  if (msaSortOrder === null) {
    return null;
  } else if (msaSortOrder === 'asc') {
    return SORT_ORDER.Asc;
  } else if (msaSortOrder === 'desc') {
    return SORT_ORDER.Desc;
  }
  return null;
};

const DEFAULT_SORT = {
  field: CERBERUS_STATS_FIELDS.listingsOnMarketCount,
  order: SORT_ORDER.Desc,
};

export const useCerberusStatsMsasTable = (options?: Options) => {
  const { data: msaList, isLoading: msaListIsLoading } =
    useCerberusStatsAllMsasWithHpi();
  const {
    state: {
      filterSet,
      filterSetQueryIsLoading,
      filterSetMutationIsLoading,
      search: msaSearch,
    },
    actions: {
      onToggleInterestedOnly,
      onSort,
      onSearch,
      onUpdateFieldFilters,
      onClearFieldFilter,
      onUpdateFilterableFields,
    },
  } = useCerberusStatsFilters(CERBERUS_STATS_FILTER_TYPE.msa);
  const {
    state: { geoInterestedMap: interestedInMsaMap, query: geoInterestedQuery },
    actions: { onToggleInterested },
  } = useGeoInterestedUiPreferences('msas');

  const [searchParams] = useSearchParams();
  const searchFromParams = searchParams.get(LEGACY_QUERY_PARAM_NAMES.Search);
  const sortFieldFromParams = searchParams.get(LEGACY_QUERY_PARAM_NAMES.Sort);
  const sortOrderFromParams = searchParams.get(LEGACY_QUERY_PARAM_NAMES.Order);
  const { fieldFilters } = filterSet || {};

  const interestedOnly = filterSet?.interestedOnly || null;
  const search = searchFromParams || msaSearch;
  let sortField = sortFieldFromParams
    ? sortParamToCerberusStatsSortField(sortFieldFromParams)
    : options?.sort.field || filterSet?.sortField;
  if (!sortField) {
    sortField = DEFAULT_SORT.field;
  }
  let sortOrder = sortOrderFromParams
    ? sortOrderParamToSortOrderEnum(sortOrderFromParams)
    : options?.sort.order || filterSet?.sortOrder;
  if (!sortOrder) {
    sortOrder = DEFAULT_SORT.order;
  }

  const filteredList = useMemo(() => {
    return filterList({
      list: msaList,
      fieldFilters,
      filterSetIsLoading: filterSetQueryIsLoading,
      search,
      interestedOnly,
      interestedInMap: interestedInMsaMap,
    });
  }, [
    msaList,
    fieldFilters,
    filterSetQueryIsLoading,
    search,
    interestedOnly,
    interestedInMsaMap,
  ]);

  const filteredSortedList = useMemo(() => {
    return sortList({
      list: filteredList,
      sortField,
      sortOrder,
      filterSetIsLoading: filterSetQueryIsLoading,
    });
  }, [filteredList, sortField, sortOrder, filterSetQueryIsLoading]);

  return {
    state: {
      isLoading:
        filterSetQueryIsLoading ||
        msaListIsLoading ||
        geoInterestedQuery.isLoading,
      filterSetMutationIsLoading,
      filteredSortedList,
      totalCount: msaList?.length,
      interestedInMsaMap,
      search,
      sortField,
      sortOrder,
      fieldFilters: filterSet?.fieldFilters,
      interestedOnly: filterSet?.interestedOnly,
    },
    actions: {
      onToggleInterestedOnly,
      onSort,
      onToggleMsaInterested: onToggleInterested,
      onSearch,
      onUpdateFieldFilters,
      onClearFieldFilter,
      onUpdateFilterableFields,
    },
  };
};
